import '../../../../dataSources/components/StatusWidget/status-widget.less'
import {
  resetWidget,
  fetchWidgetStatus,
  ACTION_WIDGET_STATUS,
  IGetWidgetStatusParams,
  WidgetStatus
} from '../../../../dataSources/components/StatusWidget/statusSlice'
import StatusLabel from '../../../../dataSources/components/StatusWidget/statusLabel'
import LoaderView from '../../../../../components/Loader/loader'
import SvgNoData from '../../../../../assets/inlineSvg/not_data_found.svg'
import AlertInfoIcon from '../../../../../assets/inlineSvg/icon-alert-info.svg'
import { RootState } from '../../../../../rootReducer'
import { ScanStatus } from '../../../../../interfaces'
import { getShowLoader } from '../../../../../reducers/requestReducer'
import useUrlContext from '../../../../../hooks/useUrlContext'
import { DATA_SOURCE_TYPES, RESCAN_SUPPORTED_DATASOURCES } from '../../../../../constants'
import {
  ChartColors,
  getUniqueWidgetIndex,
  STATUS_SCAN_COLORS,
  UNIQUE_WIDGET_INDEX
} from '../../../mainDashboardv2/constants'
import { isDatasourceFileCountersEnabled } from '../../../../../utils/dataSourceUtil'
import { mockDatasourceOverviewStatusWidgetData } from '../../../renderer/utils/widgetMockdata'
import { connect } from 'react-redux'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import React, { useEffect, useMemo } from 'react'
import { Icon, Tooltip, Typography } from '@lightbeamai/design-system'

const FALLBACK_COLOR = '#001529'

interface OwnProps {
  useMockData?: boolean
  className?: string
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
}

interface StateProps {
  widgetData: WidgetStatus
  loader: boolean
}

interface DispatchProps {
  fetchWidgetStatus: (params: IGetWidgetStatusParams & { [UNIQUE_WIDGET_INDEX]?: string }) => void
  resetWidget: () => void
}

type StatusWidgetComponentProps = StateProps & DispatchProps & OwnProps

const StatusWidget = ({
  loader,
  widgetData: {
    status = ScanStatus.registering,
    objectsCount = {
      processedCount: 0,
      skippedCount: 0,
      pendingCount: 0,
      rescanPendingCount: 0
    }
  },
  resetWidget,
  fetchWidgetStatus,
  className,
  useMockData = false,
  widgetDatasourceId,
  widgetDatasourceType,
  widgetDashboardId
}: StatusWidgetComponentProps) => {
  const intl = useIntl()
  const context = useUrlContext()
  const dataSourceType = widgetDatasourceType
    ? widgetDatasourceType
    : context.queryParams.datasourceType

  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  const isJira = dataSourceType === DATA_SOURCE_TYPES.jira
  const isCopilot = dataSourceType === DATA_SOURCE_TYPES.copilot
  const statusLowecase = status.toLowerCase() as ScanStatus
  const isRegistering = statusLowecase === ScanStatus.registering
  const jiraScannedInfoText = intl.formatMessage({ id: 'dashboard.jira.widget.scanned.title' })
  const areFileCountersEnabled = useMemo(() => isDatasourceFileCountersEnabled(), [])
  const widgetData = [
    {
      text: intl.formatMessage({ id: 'widget.status.scanned' }),
      count: objectsCount.processedCount || 0,
      ...(isJira ? { tooltip: jiraScannedInfoText } : {})
    },
    {
      text: intl.formatMessage({ id: 'widget.status.pending' }),
      count: objectsCount.pendingCount || 0,
      ...(isCopilot ? { isVisible: false } : {})
    },
    {
      text: intl.formatMessage({ id: 'widget.status.skipped' }),
      count: objectsCount.skippedCount || 0,
      tooltip: intl.formatMessage({ id: 'widget.status.skipped.tooltip' }),
      isVisible: areFileCountersEnabled
    }
  ]
  useEffect(() => {
    if (useMockData) return
    if (context.contextFilters.datasourceId || widgetDatasourceId) {
      const params = {
        datasourceId: widgetDatasourceId
          ? widgetDatasourceId
          : (context.contextFilters.datasourceId as string),
        datasourceType: dataSourceType as string,
        [UNIQUE_WIDGET_INDEX]: widgetIndex
      }

      fetchWidgetStatus(params)
    }

    return () => resetWidget()
  }, [
    context,
    dataSourceType,
    fetchWidgetStatus,
    resetWidget,
    useMockData,
    widgetIndex,
    widgetDatasourceId
  ])

  return (
    <div className={`status-widget-compact ${className}`} data-test-id="datasources-status-widget">
      <div className="widget-status-info">
        <header>
          <Typography variant="baseMinus2" color="custom" type="medium" as="span">
            <FormattedMessage id="dashboard.slack.widget.text.STATUS" />
          </Typography>
          <StatusLabel status={status} dataSourceType={dataSourceType as string} />
        </header>

        {isRegistering ? (
          <div>
            <div className="icon-box">
              <SvgNoData />
            </div>
            <div className="empty-state-text">
              {intl.formatMessage({ id: 'dashboard.datasource.noscanned' })}
            </div>
          </div>
        ) : (
          <>
            {status ? (
              <>
                <div className="summary-count-container">
                  {widgetData
                    .filter((item) => item.isVisible ?? true)
                    .map((item, index) => (
                      <div className="summary-count" key={item.text}>
                        <div
                          className="status-line"
                          style={{
                            backgroundColor:
                              (STATUS_SCAN_COLORS && STATUS_SCAN_COLORS.length > index
                                ? STATUS_SCAN_COLORS[index]
                                : undefined) ?? FALLBACK_COLOR
                          }}
                        ></div>
                        <Typography variant="baseMinus2" className="font-800" as="span">
                          <FormattedNumber value={item.count ?? 0} notation="compact" />
                        </Typography>
                        <Typography
                          variant="baseMinus2"
                          className="summary-count-title"
                          as="span"
                          title={item.text}
                        >
                          {item.text}
                        </Typography>
                        {item.tooltip && (
                          <Tooltip title={item.tooltip}>
                            <AlertInfoIcon className="summary-count-tooltip" />
                          </Tooltip>
                        )}
                      </div>
                    ))}
                </div>
                {RESCAN_SUPPORTED_DATASOURCES.includes(dataSourceType as DATA_SOURCE_TYPES) && (
                  <>
                    <hr className="rescan-status-top-divider" />
                    <div className="rescan-status">
                      <div className="rescan-status-content">
                        <Typography variant="baseMinus2" className="xs-mb-0">
                          {intl.formatMessage({ id: 'widget.status.rescanStatus' })}
                        </Typography>
                        <div className="align-center">
                          <Tooltip
                            placement="bottom"
                            title={
                              <div>
                                <Typography color="white" className="xs-mb-0">
                                  {intl.formatMessage({
                                    id: 'widget.status.filesPending.title.heading'
                                  })}
                                </Typography>
                                <Typography color="white" variant="baseMinus2">
                                  {intl.formatMessage({
                                    id: 'widget.status.filesPending.title.subheading'
                                  })}
                                </Typography>
                              </div>
                            }
                          >
                            <Icon name="AlertCircle" size={16} />
                          </Tooltip>
                          <Typography className="xs-mb-0" variant="baseMinus2">
                            :
                          </Typography>
                        </div>
                        <div
                          className="status-line"
                          style={{ backgroundColor: ChartColors.Red }}
                        ></div>
                        <Typography
                          className="xs-mb-0 font-800"
                          type="strong"
                          as="span"
                          variant="baseMinus2"
                        >
                          <FormattedNumber
                            value={objectsCount.rescanPendingCount ?? 0}
                            notation="compact"
                          />
                        </Typography>
                        <Typography className="xs-mb-0" variant="baseMinus2">
                          {intl.formatMessage({ id: 'widget.status.filesPending' })}
                        </Typography>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="no-summary-data">
                <div className="icon-box">
                  <SvgNoData />
                </div>
                <div className="empty-state-text">
                  {intl.formatMessage({ id: 'dashboard.widget.noSensitiveData' })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <LoaderView showLoader={loader} size="small" />
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
  return {
    widgetData: ownProps?.useMockData
      ? mockDatasourceOverviewStatusWidgetData.widgetData
      : state.datasourceStatus?.widget?.widgetDataStatus?.[widgetIndex] || {},
    loader: getShowLoader(ACTION_WIDGET_STATUS)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchWidgetStatus,
  resetWidget
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(StatusWidget)
